/**
 * Rights for somebody whose only relationship is to one project.
 */

import { AccountType } from './dto/account';
import { type Rights, UserType } from './state/account/types';

/** What the resolution needs to know about the viewer and the project in front of them. */
export interface ProjectRightsInput {
  /** The viewer's id. */
  userId?: string | null;
  /** `isProjectProvider(project, me.id)` - the project's own answer about who its provider is. */
  projectProvider?: boolean;
  /** The account the project is being viewed through, where one is known. */
  accountId?: string | null;
}

/**
 * The rights to use on a project surface.
 *
 * `useRights` returns undefined for a project-scoped guest, and correctly: the rights object is
 * built from the company roster, the server refuses them anything addressed to the account, and so
 * the roster is deliberately never fetched. Nothing about a company can be answered for them.
 *
 * A project can be answered for them. They were invited to one, the project names them as its
 * provider, and the work of pricing it is the job they were sent. So where rights do not resolve,
 * this fills in the two answers the project itself supports and nothing else.
 *
 * Deliberately not a general resolve. Every account-level flag stays false, because they are
 * questions about a firm this person has not claimed: its profile, its offices, its staff, its
 * subscription, creating work on its behalf. `canUseAiHub` stays false with them - the AI Hub is
 * claim-gated by ruling. `isGuest` stays true, so anything reading it still knows what this is.
 * This is the same line ProjectService's patch gate draws on the server: a project membership
 * authorizes project work, and a company roster is the only thing that authorizes company work.
 * @param rights - What `useRights` answered.
 * @param input - The viewer and the project.
 * @returns The rights for this project surface, or undefined when there are none.
 */
export const resolveProjectRights = (rights: Rights | undefined, input: ProjectRightsInput): Rights | undefined => {
  // A firm that owns its account answers through rights, exactly as it did before. Returned by
  // reference so a claimed firm's rights cannot drift through here.
  if (rights) return rights;

  if (!input.projectProvider || !input.userId) return undefined;

  return {
    id: input.userId,
    accountId: input.accountId ?? undefined,

    // The project's answer, scoped to this project. Not a claim about the firm.
    userType: UserType.provider,
    accountType: AccountType.Provider,
    canViewFees: true,

    // Still a guest, and still not the primary provider.
    isGuest: true,
    primaryProvider: false,

    // Deciding the client's fee belongs to the client. A provider never gets it.
    canDecideFee: false,

    offices: [],
    clients: [],
    estimateApps: [],
    projects: [],
  };
};
